/**
 * BancoDadosPanel.jsx — Rê Leve
 * Aba "Banco de Dados" da Janela do Operador.
 *
 * Mostra todas as "tabelas" (chaves do localStorage) em formato auditável:
 * registros, tamanho, conteúdo expandível, export/limpar individual.
 *
 * Mensagem clara: hoje em browser → amanhã Postgres quando backend ativar.
 */

import { useEffect, useState } from 'react';
import {
  getAllTables, getStorageUsage,
  clearTable, clearAllTables,
  exportTable, exportAllTables, importAllTables,
  formatBytes,
} from '../lib/databaseInspector';
import { subscribeAll } from '../lib/liveSyncEngine';

export default function BancoDadosPanel() {
  const [tick, setTick] = useState(0);
  const [expanded, setExpanded] = useState(null);
  const [importText, setImportText] = useState('');
  const [showImport, setShowImport] = useState(false);

  const tables = getAllTables();
  const usage  = getStorageUsage();

  useEffect(() => {
    const unsub = subscribeAll(() => setTick(t => t + 1));
    return () => unsub();
  }, []);

  function handleClearTable(t) {
    if (confirm(`Limpar a tabela "${t.name}"? Os dados deste navegador serão apagados.`)) {
      clearTable(t.key);
      setTick(x => x + 1);
    }
  }

  function handleClearAll() {
    if (confirm('LIMPAR TUDO? Apaga todas as tabelas deste navegador. Sem volta sem backup.')) {
      clearAllTables();
      setTick(x => x + 1);
    }
  }

  function handleImport() {
    const result = importAllTables(importText);
    if (result.ok) {
      alert(`${result.count} tabelas importadas!`);
      setImportText('');
      setShowImport(false);
      setTick(x => x + 1);
    } else {
      alert('Erro: ' + result.error);
    }
  }

  return (
    <div className="rl-ops-panel">
      <div className="rl-ops-header">
        <div>
          <div className="rl-ops-eyebrow">Banco de Dados · estado atual</div>
          <h1 className="rl-ops-title">As tabelas da Rê Leve</h1>
          <p className="rl-ops-sub">
            <strong>Hoje:</strong> cada tabela vive no <code>localStorage</code> do seu navegador
            (você pode ver no DevTools: F12 → Application → Local Storage).
            <br/>
            <strong>Amanhã:</strong> quando você configurar a chave do Neon na aba Credenciais,
            esses dados migram para PostgreSQL real e ficam sincronizados entre dispositivos.
          </p>
        </div>
        <div className="rl-ops-actions">
          <button className="rl-btn rl-btn--small" onClick={exportAllTables}>⇩ Backup completo (JSON)</button>
          <button className="rl-btn rl-btn--ghost rl-btn--small" onClick={() => setShowImport(s => !s)}>
            {showImport ? '✕ Cancelar import' : '⇧ Importar backup'}
          </button>
          <button className="rl-btn rl-btn--ghost rl-btn--small" onClick={handleClearAll} style={{ color: 'var(--rl-tomate)' }}>
            🗑 Limpar tudo
          </button>
        </div>
      </div>

      {showImport && (
        <section className="rl-card rl-card--mostard">
          <div className="rl-card-eyebrow">Importar backup JSON</div>
          <textarea
            className="rl-input rl-input--text"
            rows={5}
            value={importText}
            onChange={e => setImportText(e.target.value)}
            placeholder='Cole aqui o conteúdo de um arquivo de backup ({"tables": {...}})'
            style={{ minHeight: 120, fontFamily: 'var(--rl-font-mono)', fontSize: 11 }}
          />
          <div className="rl-form-actions">
            <button className="rl-btn rl-btn--small" onClick={handleImport} disabled={!importText.trim()}>
              ✓ Importar agora
            </button>
          </div>
        </section>
      )}

      {/* KPIs */}
      <div className="rl-kpi-grid">
        <div className="rl-kpi">
          <div className="rl-kpi-label">Tabelas catalogadas</div>
          <div className="rl-kpi-value">{tables.length}</div>
          <div className="rl-kpi-sub">{tables.filter(t => t.exists).length} populadas</div>
        </div>
        <div className="rl-kpi">
          <div className="rl-kpi-label">Total de registros</div>
          <div className="rl-kpi-value">{tables.reduce((s, t) => s + (t.count || 0), 0)}</div>
          <div className="rl-kpi-sub">somando tudo</div>
        </div>
        <div className="rl-kpi">
          <div className="rl-kpi-label">Espaço usado</div>
          <div className="rl-kpi-value">{formatBytes(usage.used)}</div>
          <div className="rl-kpi-sub">de ~{formatBytes(usage.quota)} ({usage.pct.toFixed(1)}%)</div>
        </div>
        <div className="rl-kpi">
          <div className="rl-kpi-label">Origin atual</div>
          <div className="rl-kpi-value" style={{ fontSize: 14, fontFamily: 'var(--rl-font-mono)' }}>
            {typeof window !== 'undefined' ? window.location.host : '—'}
          </div>
          <div className="rl-kpi-sub">localStorage é por origin</div>
        </div>
      </div>

      {/* Lista de tabelas */}
      <div className="rl-tables-grid">
        {tables.map(t => (
          <article key={t.key} className={'rl-table-card' + (t.exists ? '' : ' rl-table-card--empty')}>
            <div className="rl-table-head">
              <div className="rl-table-icon">{t.icon}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="rl-table-name">{t.name}</div>
                <code className="rl-table-key">{t.key}</code>
              </div>
              <div className="rl-table-stats">
                <div className="rl-table-count">{t.count}</div>
                <div className="rl-table-size">{formatBytes(t.size)}</div>
              </div>
            </div>

            <div className="rl-table-desc">{t.description}</div>
            <div className="rl-table-schema">
              <strong>Schema:</strong> <code>{t.schema}</code>
            </div>
            <div className="rl-table-future">
              📦 Futuro: <code>{t.futureBackend}</code>
            </div>

            <div className="rl-table-actions">
              <button
                className="rl-link"
                onClick={() => setExpanded(expanded === t.key ? null : t.key)}
                disabled={!t.exists}
              >
                {expanded === t.key ? '▼ Esconder dados' : '▶ Ver dados'}
              </button>
              <button className="rl-link" onClick={() => exportTable(t.key)} disabled={!t.exists}>
                ⇩ Exportar JSON
              </button>
              <button className="rl-link rl-link--danger" onClick={() => handleClearTable(t)} disabled={!t.exists}>
                🗑 Limpar tabela
              </button>
            </div>

            {expanded === t.key && (
              <div className="rl-table-content">
                <pre>{JSON.stringify(t.parsed, null, 2)}</pre>
              </div>
            )}
          </article>
        ))}
      </div>

      {/* Aviso */}
      <section className="rl-card rl-card--mostard">
        <div className="rl-card-head">
          <span className="rl-leve-avatar">🌱</span>
          <div>
            <div className="rl-card-eyebrow">Importante saber</div>
            <div className="rl-card-title">localStorage hoje, Postgres amanhã</div>
          </div>
        </div>
        <ul className="rl-quick-actions">
          <li>📍 Dados são <strong>por navegador + por origin</strong>. Se você abre em outro PC, não vê esses dados.</li>
          <li>🔄 Sync entre abas do mesmo PC funciona via BroadcastChannel (você já tá vendo isso na aba Conversas).</li>
          <li>🗑 Limpar histórico do browser apaga tudo. Faça <strong>Backup completo</strong> regularmente.</li>
          <li>🚀 Quando você setar <code>DATABASE_URL</code> do Neon no Render (aba Credenciais), os engines apontam pro backend e tudo vira tabela Postgres real.</li>
          <li>📥 Posso importar um backup em qualquer momento — útil para mover dados entre navegadores agora.</li>
        </ul>
      </section>
    </div>
  );
}
